// Localização do usuário para o header `X-Location` do login do Portal Médico.
//
// A permissão é pedida na tela de login (quando o perfil "Portal Médico" é
// selecionado), não no envio do formulário: assim o diálogo do navegador
// aparece enquanto o médico digita e a resposta já está em mãos na hora do
// POST de login.
//
// Nada aqui rejeita. Sem permissão, sem suporte do navegador ou sem sinal, a
// promessa resolve `null` e o login segue sem o header — a geolocalização é um
// dado extra, não um requisito para entrar.

/** Tempo máximo esperando o `getCurrentPosition` do navegador. */
const TIMEOUT_MS = 10000;

/**
 * Pedido em andamento (ou já concluído), compartilhado entre a tela de login e
 * o `portalMedicosLogin()`: o diálogo de permissão é exibido uma única vez e a
 * mesma resposta serve para o envio do formulário.
 */
let inFlight: Promise<string | null> | null = null;

/**
 * `latitude,longitude` no formato aceito pelo header `X-Location`
 * (ex.: `-26.9194,-49.0661`), com 4 casas decimais — precisão de ~11 m, o
 * bastante para identificar a cidade sem expor o endereço exato.
 */
const format = ({ latitude, longitude }: GeolocationCoordinates): string =>
  `${latitude.toFixed(4)},${longitude.toFixed(4)}`;

/**
 * Pede a localização ao navegador. Deve ser chamada a partir da tela de login,
 * que é quando queremos que o diálogo de permissão apareça.
 *
 * O resultado fica em cache para a sessão da página: chamadas seguintes
 * (remontagem da tela, nova tentativa de login) reaproveitam a mesma promessa
 * em vez de pedir de novo.
 */
export const requestLocation = (): Promise<string | null> => {
  if (!inFlight) {
    inFlight = new Promise<string | null>((resolve) => {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => resolve(format(position.coords)),
        // Permissão negada, posição indisponível ou tempo esgotado: seguimos sem
        () => resolve(null),
        { timeout: TIMEOUT_MS },
      );
    });
  }

  return inFlight;
};

/**
 * Localização já obtida pela tela de login, para montar o header `X-Location`.
 * Se o usuário ainda não respondeu ao diálogo de permissão, espera a resposta
 * (no máximo o `TIMEOUT_MS` do pedido em andamento). Retorna `null` quando não
 * há localização a enviar.
 */
export const getLocationHeader = (): Promise<string | null> =>
  inFlight || Promise.resolve(null);
